import React from 'react';
import { Link } from 'react-router-dom'
import { Table, Tag, Space, Card, Button, Modal, Form, Input, message, Tooltip } from 'antd';
import {
  EditTwoTone,
  DeleteTwoTone
} from '@ant-design/icons';
import axios from '../services/axios'

const Users = () => {
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [resetTarget, setResetTarget] = React.useState<any>(null)
  const [blockTarget, setBlockTarget] = React.useState<any>(null)
  const [form] = Form.useForm()
  const [blockForm] = Form.useForm()

  React.useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = () => {
    setLoading(true)
    axios.get('/users')
      .then(response => {
        setData(response.data);
        setLoading(false)
      });
  }

  const openBlock = (record: any) => {
    blockForm.resetFields();
    setBlockTarget(record);
  }

  const submitBlock = (values: any) => {
    axios.post(`/users/${blockTarget.id}/block`, { reason: values.reason })
      .then(() => {
        message.success("Utilisateur bloqué avec succès");
        setBlockTarget(null);
        fetchUsers();
      })
      .catch(error => {
        message.error(error.response?.data?.error || "Echec de l'opération");
      });
  }

  const unblock = (record: any) => {
    axios.post(`/users/${record.id}/unblock`)
      .then(() => {
        message.success("Utilisateur débloqué avec succès");
        fetchUsers();
      })
      .catch(error => {
        message.error(error.response?.data?.error || "Echec de l'opération");
      });
  }

  const openResetPassword = (record: any) => {
    form.resetFields();
    setResetTarget(record);
  }

  const submitResetPassword = (values: any) => {
    axios.post(`/users/${resetTarget.id}/reset-password`, values)
      .then(() => {
        message.success("Mot de passe réinitialisé avec succès");
        setResetTarget(null);
      })
      .catch(error => {
        message.error(error.response?.data?.error || "Echec de la réinitialisation");
      });
  }

  const columns = [
    {
      title: 'N°',
      dataIndex: 'key',
      key: 'key',
      render: (text: string) => <a>{text}</a>,
    },
    {
      title: 'Prenom',
      dataIndex: 'prenom',
      key: 'prenom',
    },
    {
      title: 'Nom',
      dataIndex: 'nom',
      key: 'nom',
    },
    {
      title: 'Téléphone',
      dataIndex: 'telephone',
      key: 'telephone',
    },
    {
      title: 'Nom d\'utilisateur',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Role',
      key: 'roles',
      render: (record: any) => <Tag>{record.roles[0] ? record.roles[0].name : ""}</Tag>,
    },
    {
      title: 'Statut',
      key: 'status',
      render: (record: any) => {
        const tag = (
          <Tag color={record.status === 1 ? 'green' : 'red'}>
            {record.status === 1 ? 'Actif' : 'Bloqué'}
          </Tag>
        );

        if (record.status === 1 || !record.blocked_at) {
          return tag;
        }

        const blockedBy = record.blocked_by_user
          ? `${record.blocked_by_user.prenom} ${record.blocked_by_user.nom}`
          : null;

        return (
          <Tooltip
            title={
              <>
                {blockedBy && <div>Par : {blockedBy}</div>}
                <div>Le : {new Date(record.blocked_at).toLocaleString()}</div>
                {record.blocked_reason && <div>Raison : {record.blocked_reason}</div>}
              </>
            }
          >
            {tag}
          </Tooltip>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (text: string, record: any) => (
        <Space size="middle">
          <Link to={`/add-users/edit/${record.id}`}><EditTwoTone /> Editer</Link>
          <Button
            danger={record.status === 1}
            onClick={() => record.status === 1 ? openBlock(record) : unblock(record)}
          >
            <DeleteTwoTone /> {record.status === 1 ? 'Bloquer' : 'Débloquer'}
          </Button>
          <Button type="primary" onClick={() => openResetPassword(record)}>Réinitialiser mot de passe</Button>
        </Space>
      ),
    },
  ];

  return <>
    <Card size="small" title="La liste des utilisateurs" extra={<Link to="/add-users">Nouveau</Link>} >
      <Table columns={columns} dataSource={data} loading={loading} />
    </Card>
    <Modal
      title={blockTarget ? `Bloquer ${blockTarget.prenom} ${blockTarget.nom}` : 'Bloquer'}
      open={!!blockTarget}
      onCancel={() => setBlockTarget(null)}
      onOk={() => blockForm.submit()}
      okText="Bloquer"
      okButtonProps={{ danger: true }}
      cancelText="Annuler"
      destroyOnClose
    >
      <Form form={blockForm} layout="vertical" onFinish={submitBlock}>
        <Form.Item name="reason" label="Raison (optionnel)">
          <Input.TextArea rows={3} placeholder="Motif du blocage" />
        </Form.Item>
      </Form>
    </Modal>
    <Modal
      title={resetTarget ? `Réinitialiser le mot de passe de ${resetTarget.prenom} ${resetTarget.nom}` : 'Réinitialiser le mot de passe'}
      open={!!resetTarget}
      onCancel={() => setResetTarget(null)}
      onOk={() => form.submit()}
      okText="Valider"
      cancelText="Annuler"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={submitResetPassword}>
        <Form.Item
          name="password"
          label="Nouveau mot de passe"
          hasFeedback
          rules={[
            { required: true, message: 'Ce champ est obligatoire!' },
            { min: 6, message: 'Le mot de passe doit contenir au moins 6 caractères' },
          ]}
        >
          <Input.Password />
        </Form.Item>
        <Form.Item
          name="password_confirmation"
          label="Confirmer le mot de passe"
          dependencies={['password']}
          hasFeedback
          rules={[
            { required: true, message: 'Ce champ est obligatoire!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject('Les deux mots de passe ne correspondent pas!');
              },
            }),
          ]}
        >
          <Input.Password />
        </Form.Item>
      </Form>
    </Modal>
  </>;
}

export default Users;
